import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI'
import WmrcResponseTimeTable from './Table/WmrcResponseTime'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import axios from 'axios'
import { useSelector } from 'react-redux'
import toasters from '../../../../../../../utils/toasters/Toasters'
import { useSearchParams } from 'react-router-dom';

const formatPayloadDates = (payload) => {
    if (!payload) return payload;

    const formattedPayload = { ...payload };

    if (formattedPayload.startDate) {
        const startDate = new Date(formattedPayload.startDate);
        if (!isNaN(startDate)) {
            formattedPayload.startDate = startDate.toISOString().split('T')[0];
        }
    }

    if (formattedPayload.endDate) {
        const endDate = new Date(formattedPayload.endDate);
        if (!isNaN(endDate)) {
            formattedPayload.endDate = endDate.toISOString().split('T')[0];
        }
    }

    return formattedPayload;
};

const WmrcResponseTime = () => {
    const location = useLocation();
    const { token } = useSelector((state) => state?.auth);
     const [searchParams] = useSearchParams();

   
    const [storedPayload, setStoredPayload] = useState(null);

    useEffect(() => {

        // URL params (direct URL support)
        const startDate = searchParams.get('startDate');
        const endDate = searchParams.get('endDate');
        const code = searchParams.get('code');

        if (startDate || endDate) {
            const urlPayload = { startDate, endDate, code };
            setStoredPayload(urlPayload);
            sessionStorage.setItem(
                'wmrcResponseTimePayload',
                JSON.stringify(urlPayload)
            );
            return;
        }

        // location.state (normal navigation)
        if (location.state) {
            setStoredPayload(location.state);
            sessionStorage.setItem(
                'wmrcResponseTimePayload',
                JSON.stringify(location.state)
            );
            return;
        }

        // sessionStorage fallback (page refresh)
        const stored = sessionStorage.getItem('wmrcResponseTimePayload');
        if (stored) {
            setStoredPayload(JSON.parse(stored));
        }

    }, [location.state, searchParams]);

    const formattedPayload = formatPayloadDates({ ...storedPayload, export: 'N' });

    const { data: WmrcResponseTime, isLoading } = useQuery(
        ['fetch-wmrc-response-time', formattedPayload],
        () => fetchIncidentAnalysisData(formattedPayload),
        {
          enabled: !!storedPayload,
          retry: (failureCount, error) => {
            const status = error?.response?.status;
            return failureCount < 1 && status >= 400 && status < 600;
          },
          retryDelay: 1000, 
        }
      );
      

    // PDF download function
    const downloadPDF = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: [297, 210],
        });

        doc.setFontSize(18);
        doc.text('WMRC Response Time Report', 14, 22);

        doc.setFontSize(12);
        doc.text(
            `Date Range: ${formattedPayload.startDate} - ${formattedPayload.endDate}`,
            14,
            30
        );

        const tableColumn = ['All WMRC RESPONSE Time', 'Selected WMRC RESPONSE Time'];

        const tableRows =
            WmrcResponseTime?.map((item) => [
                item?.td_allWmrcTime || '',
                item?.td_myWmrcTime || '',
            ]) || [];

       doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: 40,
    theme: 'grid',
    // 1. Force the table to span the full width of the margins
    margin: { left: 14, right: 14 }, 
    tableWidth: 'auto', 
    
    headStyles: {
        fillColor: [0, 81, 242],
        textColor: [255, 255, 255],
        fontSize: 12,
        halign: 'center' // Optional: Center headers for better look
    },
    bodyStyles: {
        fontSize: 10,
        cellPadding: 3,
        halign: 'center' // Optional: Center body text
    },
    alternateRowStyles: {
        fillColor: [245, 245, 245],
    },
    styles: {
        lineColor: [0, 0, 0],
        lineWidth: 0.1, // Reduced width for a cleaner look
    },
    // 2. Remove fixed cellWidth or use 'factor' to split 50/50
    columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 'auto' },
    },
});

        doc.save('wmrc-response-time-report.pdf');
    };

    const downloadFile = (data, fileName = 'download') => {
        const csvBlob = new Blob([data], { type: 'text/csv' });
        const csvUrl = URL.createObjectURL(csvBlob);
        const hiddenElement = document.createElement('a');
        hiddenElement.href = csvUrl;
        hiddenElement.target = '_blank';
        hiddenElement.download = `${fileName}.csv`;
        hiddenElement.click();
    };

    const ExcelWmrcResponseTime = async () => {
        try {
            let response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                { ...formattedPayload, export: 'Y' },
                {
                    headers: {
                        'Content-type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            );
            downloadFile(response?.data, 'wmrc-response-time-report');
        } catch (error) {
            toasters({ type: 'error', message: error?.response?.data?.message });
        }
    };

    return (
        <>
            <WmrcResponseTimeTable
                isLoading={isLoading}
                WmrcResponseTime={WmrcResponseTime}
                DisplayDate={formattedPayload}
                downloadPDF={downloadPDF}
                downloadExcel={ExcelWmrcResponseTime}
                reportData={formattedPayload}
                reportCode={storedPayload?.code || 'wmrcResponseTime'}
                reportTableData={WmrcResponseTime}
                generatePDFFunction={downloadPDF}
            />
        </>
    );
};

export default WmrcResponseTime;
